import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import styled from 'styled-components';
import { hot } from 'react-hot-loader';
import { Link, useLocation } from 'react-router-dom';
import { Card, Header, Dimmer, Loader, Button, Icon, Input, Divider } from 'semantic-ui-react';

import { withLocale } from 'op/components/locale';
import { withAuditList } from './audit-list.helper';
import getDateTimeString from 'op/utils/getDateTimeString';

const StyledPage = styled.div`
  min-height: calc(100vh - 150px);
`;

const Search = styled(Input)`
	width: 100%;
	border: 0;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledTable = styled.table`
    width: 100%;
    min-width: 800px;
    border-collapse: collapse;
    border-spacing: 0;
    border: 1px solid #ddd;
    margin-bottom: 1em;
    thead {
        background-color: #f2f2f2;
        tr {
            th {
                padding: 0.5em;
                text-align: left;
                white-space: nowrap;
            }
        }
    }
    tbody {
        tr {
            td {
                padding: 0.5em;
                border-top: 1px solid #ddd;
                background-color: #fff;
                border-left: 1px solid #ddd;
                white-space: nowrap;
                
                a {
                    color: #4183c4;
                    text-decoration: none;
                    &:hover {
                        color: #1e70bf;
                        text-decoration: underline;
                    }
                }
            }
        }
    }
`;

const ActionBar = styled.div`
	display: inline;
  position: fixed;
  top: calc(100vh - 20px - 4em);
  ${props => props.direction || 'right'}: 20px;
  z-index: 3000;
  -webkit-backface-visibility: hidden;
`;

const AuditList = ({ locale, audits, loading, fetchAudits, online, setLoading }) => {
  const [filter, setFilter] = useState('');
  const [filteredAudits, setFilteredAudits] = useState([]);
  const location = useLocation();

  useEffect(() => {
    if (online && fetchAudits) {
      fetchAudits();
    } else if (!online && setLoading) {
      setLoading(false);
    }
  }, [fetchAudits, location.key, online, setLoading]);

  useEffect(() => {
    if (audits) {
      const lowerFilter = filter.toLowerCase();
      const filtered = audits.filter(audit =>
        (audit.rdS_Unit_Name && audit.rdS_Unit_Name.toLowerCase().includes(lowerFilter)) ||
        (audit.inS_MainCategoryName && audit.inS_MainCategoryName.toLowerCase().includes(lowerFilter)) ||
        (audit.inS_SubCategoryName && audit.inS_SubCategoryName.toLowerCase().includes(lowerFilter)) ||
        (audit.inS_MainAuditUserName && audit.inS_MainAuditUserName.toLowerCase().includes(lowerFilter)) ||
        (audit.inS_MainAuditManagerName && audit.inS_MainAuditManagerName.toLowerCase().includes(lowerFilter)) ||
        (audit.inS_MainAuditFollowupManagerName && audit.inS_MainAuditFollowupManagerName.toLowerCase().includes(lowerFilter))
      );
      setFilteredAudits(filtered);
    }
  }, [audits, filter]);

  if (loading) {
    return (
      <StyledPage>
        <Dimmer active><Loader content={locale.messages.loading} /></Dimmer>
      </StyledPage>
    );
  }

  return (
    <StyledPage>
      <Search
        placeholder={locale.list.search.placeholder}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <Divider />

      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>{locale.list.table.headers.date}</th>
              <th>{locale.list.table.headers.unitName}</th>
              <th>{locale.list.table.headers.auditArea}</th>
              <th>{locale.list.table.headers.followupManager}</th>
              <th>{locale.list.table.headers.auditSubject}</th>
              <th>{locale.list.table.headers.auditorName}</th>
              <th>{locale.list.table.headers.unitManager}</th>
            </tr>
          </thead>
          <tbody>
            {filteredAudits.map((audit) => {
              const date = audit.inS_MainAuditDate ? new Date(audit.inS_MainAuditDate) : null;
              const formattedDate = date ?
                `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}` :
                '-';

              return (
                <tr key={audit.inS_MainAuditID}>
                  <td>
                    <Link to={`/audit/${audit.inS_MainAuditID}`}>
                      {formattedDate}
                    </Link>
                  </td>
                  <td>{audit.rdS_Unit_Name || ' '}</td>
                  <td>{audit.inS_MainCategoryName || ' '}</td>
                  <td>{audit.inS_MainAuditFollowupManagerName || ' '}</td>
                  <td>{audit.inS_SubCategoryName || ' '}</td>
                  <td>{audit.inS_MainAuditUserName || ' '}</td>
                  <td>{audit.inS_MainAuditManagerName || ' '}</td>
                </tr>
              );
            })}
            {filteredAudits.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2em' }}>
                  {locale.list.empty.noAuditsFound}
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </TableContainer>

      <ActionBar direction={locale._meta.rtl ? 'left' : 'right'}>
        <Button icon={'plus'} disabled={false} circular
          size='huge' primary
          as={Link} to="/audit/new"
        />
      </ActionBar>
    </StyledPage>
  );
};

export default compose(
  hot(module),
  withLocale('audit'),
  withAuditList
)(AuditList);
